import { TemplateType } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check } from 'lucide-react';
import { TEMPLATES } from './TemplateProvider';
import { cn } from '../ui/utils';

// Template color mapping for selector icon badges
const SELECTOR_COLORS: Record<string, string> = {
  atlassian: 'bg-blue-600',
  custom: 'bg-gray-600',
  generic: 'bg-gray-600',
  servicenow: 'bg-red-600',
  ivanti: 'bg-green-600',
  'bmc-helix': 'bg-purple-600',
  freshservice: 'bg-teal-600',
};

interface TemplateSelectorProps {
  selectedTemplate: TemplateType;
  onSelect: (template: TemplateType) => void;
  disabled?: boolean;
}

export function TemplateSelector({
  selectedTemplate,
  onSelect,
  disabled = false,
}: TemplateSelectorProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {TEMPLATES.map((template) => {
        const isSelected = selectedTemplate === template.id;
        const isDisabled = disabled || template.comingSoon;

        return (
          <Card
            key={template.id}
            className={cn(
              'cursor-pointer transition-all hover:shadow-md relative',
              isSelected && 'ring-2 ring-blue-500',
              isDisabled && 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => !isDisabled && onSelect(template.id)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold text-white flex-shrink-0 ${SELECTOR_COLORS[template.id] || 'bg-gray-600'}`}
                  >
                    {template.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      {template.description}
                    </CardDescription>
                  </div>
                </div>
                {isSelected && (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white flex-shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm">
                  <strong>Features:</strong>
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {template.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}